import React from 'react';
import { motion } from 'framer-motion';
import LoadingSpinner from '../atoms/LoadingSpinner';
import { DocumentCardSkeleton, StatCardSkeleton } from '../atoms/SkeletonLoader';

// Document Grid Loading State
export const DocumentGridLoading: React.FC<{ count?: number }> = ({ count = 8 }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {Array.from({ length: count }).map((_, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
      >
        <DocumentCardSkeleton />
      </motion.div>
    ))}
  </div>
);

// Dashboard Stats Loading State
export const DashboardStatsLoading: React.FC = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    {Array.from({ length: 4 }).map((_, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
      >
        <StatCardSkeleton />
      </motion.div>
    ))}
  </div>
);

// Full Page Loading State
export const FullPageLoading: React.FC<{ message?: string }> = ({ 
  message = 'Loading...' 
}) => (
  <div className="min-h-screen min-h-screen-mobile flex items-center justify-center bg-gray-50">
    <div className="text-center space-y-4">
      <LoadingSpinner size="xl" text={message} />
    </div>
  </div>
);

// Modal Loading State
export const ModalLoading: React.FC<{ message?: string }> = ({ 
  message = 'Processing...' 
}) => (
  <div className="flex flex-col items-center justify-center py-12 space-y-4">
    <LoadingSpinner size="lg" />
    <p className="text-gray-600 font-medium">{message}</p>
  </div>
);

// Upload Progress Loading State
export const UploadProgressLoading: React.FC<{ 
  files: string[];
  currentFile?: string;
  progress?: number;
}> = ({ files, currentFile, progress = 0 }) => (
  <div className="space-y-4">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="text-lg font-semibold text-gray-900 mt-4">
        Uploading Files to Pinata IPFS
      </p>
      <p className="text-gray-600">
        {currentFile ? `Processing: ${currentFile}` : 'Preparing upload...'}
      </p>
    </div>
    
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span>Progress</span>
        <span>{Math.round(progress)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <motion.div
          className="bg-primary-500 h-2 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </div>
    
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">Files ({files.length})</p>
      <div className="max-h-32 overflow-y-auto space-y-1">
        {files.map((file, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div className={`w-2 h-2 rounded-full ${
              file === currentFile 
                ? 'bg-primary-500 animate-pulse' 
                : 'bg-gray-300'
            }`} />
            <span className="truncate">{file}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Blockchain Transaction Loading State
export const BlockchainTransactionLoading: React.FC<{
  step: string;
  estimatedTime?: string;
}> = ({ step, estimatedTime }) => (
  <div className="text-center space-y-4">
    <div className="relative">
      <LoadingSpinner size="xl" color="primary" />
      <motion.div
        className="absolute inset-0 border-4 border-primary-200 rounded-full"
        animate={{ scale: [1, 1.2, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
    </div>
    
    <div>
      <h3 className="text-lg font-semibold text-gray-900">
        Blockchain Transaction in Progress
      </h3>
      <p className="text-gray-600 mt-1">{step}</p>
      {estimatedTime && (
        <p className="text-sm text-gray-500 mt-2">
          Estimated time: {estimatedTime}
        </p>
      )}
    </div>
    
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
      <p className="text-sm text-blue-800">
        <strong>Note:</strong> Blockchain transactions require network confirmation. 
        Please do not close this window until the transaction is complete.
      </p>
    </div>
  </div>
);

export default {
  DocumentGridLoading,
  DashboardStatsLoading,
  FullPageLoading,
  ModalLoading,
  UploadProgressLoading,
  BlockchainTransactionLoading
};